import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { HotelIcon, UserIcon, TagIcon, MessageSquareIcon, BarChart2Icon, StarIcon, AlertCircle, Loader2, RefreshCw, ToggleLeft, ToggleRight, Trash2, SettingsIcon, WifiIcon, ServerIcon, ActivityIcon, Eye, X, Pencil, Save, AlertTriangle, Sparkles, TrendingUp, Zap, History } from 'lucide-react';
import { useHotelBullStats, useRecentProfiles, useHotelBullHealth, useProfileOperations, useHotelBullActivityHistory, useHotelBullBotHealth, useHotelBullLlmStats } from '../hooks/useHotelBull.js';
import { useDiscordGuilds } from '../hooks/usePermissions.js';
import { HotelBullConfigPage } from '../components/HotelBullConfigPage.js';
import { getStatusBadgeClass, getStatusColor, getStatusTextClass } from '../lib/statusUtils.js';
import { BotPageLayout, StatCard, StatValue, StatMetric, EntityListItem } from '../components/shared/index.js';
import { getGuildIconUrl, getDefaultAvatarUrl } from '../lib/discord-utils.js';
import type { JsonValue, RoastProfile } from '../lib/api/core-services.js';
import MDEditor from '@uiw/react-md-editor';
import { Button, Badge, Card, Alert, Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter, Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Progress, Tooltip, TooltipTrigger, TooltipContent } from '@randompedestrian07/ui';
import clsx from 'clsx';

const SimpleMarkdown = ({ content }: { content: string }) => {
  const renderMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let inCodeBlock = false;
    let codeBlockContent: string[] = [];

    const flushList = () => {
      if (currentList.length > 0 && listType) {
        const ListTag = listType;
        elements.push(
          <ListTag key={elements.length} className={listType === 'ul' ? 'list-disc list-inside mb-2 space-y-1' : 'list-decimal list-inside mb-2 space-y-1'}>
            {currentList.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
          </ListTag>
        );
        currentList = [];
        listType = null;
      }
    };

    const renderInline = (line: string): React.ReactNode => {
      const parts: React.ReactNode[] = [];
      let remaining = line;
      let key = 0;

      while (remaining.length > 0) {
        const boldMatch = remaining.match(/^(.*?)(\*\*|__)(.+?)\2(.*)$/);
        if (boldMatch) {
          if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
          parts.push(<strong key={key++} className="font-semibold text-white">{boldMatch[3]}</strong>);
          remaining = boldMatch[4];
          continue;
        }

        const italicMatch = remaining.match(/^(.*?)(\*|_)([^*_]+)\2(.*)$/);
        if (italicMatch) {
          if (italicMatch[1]) parts.push(<span key={key++}>{italicMatch[1]}</span>);
          parts.push(<em key={key++} className="italic text-gray-200">{italicMatch[3]}</em>);
          remaining = italicMatch[4];
          continue;
        }

        const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)$/);
        if (codeMatch) {
          if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
          parts.push(<code key={key++} className="bg-gray-800 px-1.5 py-0.5 rounded text-indigo-300 text-xs font-mono">{codeMatch[2]}</code>);
          remaining = codeMatch[3];
          continue;
        }

        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }

      return parts.length === 1 ? parts[0] : <>{parts}</>;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={elements.length} className="bg-gray-800 p-3 rounded-lg overflow-x-auto mb-2">
              <code className="text-xs font-mono text-gray-300">{codeBlockContent.join('\n')}</code>
            </pre>
          );
          codeBlockContent = [];
          inCodeBlock = false;
        } else {
          flushList();
          inCodeBlock = true;
        }
        continue;
      }

      if (inCodeBlock) {
        codeBlockContent.push(line);
        continue;
      }

      if (line.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={elements.length} className="text-sm font-semibold text-gray-200 mt-2 mb-1">{renderInline(line.slice(4))}</h3>);
        continue;
      }
      if (line.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={elements.length} className="text-base font-semibold text-white mt-3 mb-2">{renderInline(line.slice(3))}</h2>);
        continue;
      }
      if (line.startsWith('# ')) {
        flushList();
        elements.push(<h1 key={elements.length} className="text-lg font-bold text-white mt-0 mb-2">{renderInline(line.slice(2))}</h1>);
        continue;
      }

      if (line.startsWith('> ')) {
        flushList();
        elements.push(<blockquote key={elements.length} className="border-l-2 border-indigo-500 pl-3 italic text-gray-400 my-2">{renderInline(line.slice(2))}</blockquote>);
        continue;
      }

      if (line.match(/^[-*+] /)) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        currentList.push(line.slice(2));
        continue;
      }

      if (line.match(/^\d+\. /)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        currentList.push(line.replace(/^\d+\. /, ''));
        continue;
      }

      if (line.match(/^(-{3,}|\*{3,}|_{3,})$/)) {
        flushList();
        elements.push(<hr key={elements.length} className="border-gray-700 my-3" />);
        continue;
      }

      if (line.trim() === '') {
        flushList();
        continue;
      }

      flushList();
      elements.push(<p key={elements.length} className="text-gray-300 text-sm mb-2">{renderInline(line)}</p>);
    }

    flushList();
    return elements;
  };

  return <div className="markdown-content">{renderMarkdown(content)}</div>;
};

// Utils imported from discord-utils.js

interface ProfileData {
  id?: string;
  hashedUserId?: string;
  guildId?: string;
  internalServerId?: string;
  serverName?: string;
  discordUserId?: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  personalityTraits?: string;
  personalitySummary?: string;
  analysisCount?: number;
  lastAnalyzedAt?: string;
  isEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
  _guildName?: string;
  _userAvatarUrl?: string;
  _userName?: string;
}

const formatTraits = (value: JsonValue | undefined | null) => {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value : JSON.stringify(value);
};

const UnsavedChangesModal = ({
  isOpen,
  onDiscard,
  onCancel
}: {
  isOpen: boolean;
  onDiscard: () => void;
  onCancel: () => void;
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="fixed left-1/2 top-1/2 z-[61] w-full max-w-md -translate-x-1/2 -translate-y-1/2 bg-panel-strong rounded-2xl p-6 border border-stroke shadow-2xl backdrop-blur-xl">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-warning/20 rounded-xl">
            <AlertTriangle className="text-warning" size={24} />
          </div>
          <DialogTitle className="text-xl font-bold text-text">Unsaved Changes</DialogTitle>
        </div>
        <DialogDescription className="text-muted mb-8 text-sm leading-relaxed">
          You have unsaved changes that will be lost if you leave. Are you sure you want to discard them?
        </DialogDescription>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onCancel} className="font-medium">
            Keep Editing
          </Button>
          <Button variant="destructive" onClick={onDiscard} className="font-medium px-6">
            Discard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProfileModal = ({
  profile,
  isOpen,
  onClose,
  onSave,
  onToggle,
  onDelete,
  isLoading,
  guildName,
  userAvatarUrl,
  userName,
}: {
  profile: ProfileData | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: { personalityTraits: string }) => Promise<void>;
  onToggle: () => void;
  onDelete: () => void;
  isLoading: boolean;
  guildName: string;
  userAvatarUrl: string;
  userName: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTraits, setEditedTraits] = useState('');
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setEditedTraits(profile.personalityTraits || '');
      setIsEditing(false);
    }
  }, [profile]);

  const hasChanges = isEditing && editedTraits !== (profile?.personalityTraits || '');

  const handleClose = useCallback(() => {
    if (hasChanges) {
      setShowUnsavedWarning(true);
    } else {
      setIsEditing(false);
      onClose();
    }
  }, [hasChanges, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleDiscard = () => {
    setShowUnsavedWarning(false);
    setIsEditing(false);
    setEditedTraits(profile?.personalityTraits || '');
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({ personalityTraits: editedTraits });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  if (!isOpen || !profile) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        <div className="absolute inset-0 bg-bg-solid/40 backdrop-blur-2xl animate-in fade-in duration-500" />

        <Card className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden border border-stroke shadow-[0_0_100px_rgba(0,0,0,0.5)] flex flex-col bg-panel-strong/80 backdrop-blur-3xl rounded-[2.5rem] animate-in zoom-in-95 duration-500">
          <div className="flex items-center justify-between px-10 py-8 border-b border-stroke/40 bg-gradient-to-b from-white/5 to-transparent">
            <div className="flex items-center gap-6">
              <div className="relative">
                <img
                  src={userAvatarUrl}
                  alt={userName}
                  className="w-20 h-20 rounded-[1.5rem] ring-4 ring-stroke shadow-2xl object-cover"
                />
                <div className={clsx("absolute -bottom-2 -right-2 w-6 h-6 rounded-full border-4 border-panel-strong shadow-lg", profile.isEnabled ? "bg-green" : "bg-muted")} />
              </div>
              <div>
                <h2 className="text-3xl font-black text-text tracking-tight">{userName}</h2>
                <div className="flex items-center gap-3 text-xs font-bold text-muted/60 uppercase tracking-widest mt-1">
                  <ServerIcon size={14} className="opacity-70" />
                  {guildName}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isEditing ? (
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-12 w-12 rounded-2xl bg-panel-strong/40 border-stroke shadow-lg hover:scale-105 active:scale-95 transition-all text-muted hover:text-text"
                  onClick={() => setIsEditing(true)}
                  title="Edit profile"
                >
                  <Pencil size={20} />
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    className="font-bold text-muted hover:text-text rounded-xl h-12 px-6"
                    onClick={() => {
                      setEditedTraits(profile.personalityTraits || '');
                      setIsEditing(false);
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="ghost"
                    className="font-black rounded-xl h-12 px-8 shadow-lg shadow-accent/20 !bg-accent !text-black !border-none hover:!bg-accent/90"
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                  >
                    {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} className="mr-2" />}
                    Save
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-12 w-12 rounded-2xl text-muted hover:text-text hover:bg-white/5 transition-all"
                onClick={handleClose}
              >
                <X size={24} />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-10 space-y-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className={clsx(
                  "px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] rounded-full border-stroke animate-pulse",
                  profile.isEnabled ? "bg-green/10 text-green" : "bg-red/10 text-red"
                )}>
                  {profile.isEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
                {profile.analysisCount !== undefined && profile.analysisCount > 0 && (
                  <Badge variant="secondary" className="px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-muted/60 border-stroke bg-white/5 rounded-full">
                    {profile.analysisCount} ANALYSES
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Button
                  variant="secondary"
                  className="flex-1 sm:flex-none h-12 px-6 rounded-2xl bg-panel-strong/40 border-stroke font-bold text-muted hover:text-text transition-all"
                  onClick={onToggle}
                  disabled={isLoading}
                >
                  {profile.isEnabled ? <ToggleRight size={20} className="mr-2" /> : <ToggleLeft size={20} className="mr-2" />}
                  {profile.isEnabled ? 'DISABLE' : 'ENABLE'}
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 sm:flex-none h-12 px-6 rounded-2xl font-bold bg-red/10 border-red/20 text-red hover:bg-red/20 transition-all"
                  onClick={onDelete}
                  disabled={isLoading}
                >
                  <Trash2 size={20} className="mr-2" />
                  DELETE
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-muted/40 px-1">
                Personality
              </label>
              {isEditing ? (
                <div data-color-mode="dark" className="rounded-3xl overflow-hidden border border-stroke shadow-2xl bg-panel/30">
                  <MDEditor
                    value={editedTraits}
                    onChange={(value) => setEditedTraits(value || '')}
                    height={400}
                    preview="live"
                    visibleDragbar={true}
                    textareaProps={{
                      placeholder: 'Enter personality traits (supports Markdown)...'
                    }}
                    className="!bg-transparent !border-none"
                  />
                </div>
              ) : (
                <div className="relative group/content">
                  <div className="absolute inset-0 bg-accent/5 blur-3xl rounded-full opacity-0 group-hover/content:opacity-100 transition-opacity duration-1000 -z-10" />
                  <Card className="bg-panel-strong/20 min-h-[300px] border-stroke/40 rounded-[2rem] p-8 shadow-inner ring-1 ring-white/5 backdrop-blur-md">
                    {profile.personalityTraits ? (
                      <div className="prose prose-invert prose-p:text-text/80 prose-headings:text-text max-w-none text-text/80">
                        <SimpleMarkdown content={String(profile.personalityTraits)} />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20 text-muted/30">
                        <Sparkles size={48} className="mb-4 opacity-20" />
                        <p className="text-xs font-bold uppercase tracking-widest italic font-mono">No personality set.</p>
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="p-6 bg-panel-strong/40 border border-stroke/40 rounded-3xl group/meta">
                <div className="text-[9px] font-black text-muted/30 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-accent" />
                  Created
                </div>
                <div className="text-sm font-black text-text/80">
                  {profile.createdAt ? new Date(profile.createdAt).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' }) : 'UNKNOWN'}
                </div>
              </div>
              <div className="p-6 bg-panel-strong/40 border border-stroke/40 rounded-3xl group/meta">
                <div className="text-[9px] font-black text-muted/30 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-warning" />
                  Last Updated
                </div>
                <div className="text-sm font-black text-text/80">
                  {profile.updatedAt ? new Date(profile.updatedAt).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' }) : 'UNKNOWN'}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <UnsavedChangesModal
        isOpen={showUnsavedWarning}
        onDiscard={handleDiscard}
        onCancel={() => setShowUnsavedWarning(false)}
      />
    </>
  );
};

export const HotelBullPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedGuild, setSelectedGuild] = useState<string | undefined>(undefined);
  const [profileLimit, setProfileLimit] = useState(10);
  const [selectedProfile, setSelectedProfile] = useState<ProfileData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: health, isLoading: healthLoading, error: healthError } = useHotelBullHealth();
  const { data: botHealth, isLoading: botHealthLoading, error: botHealthError } = useHotelBullBotHealth();
  const { data: llmStats, isLoading: llmStatsLoading, error: llmStatsError, refetch: refetchLlmStats } = useHotelBullLlmStats(24);
  const { data: stats, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useHotelBullStats(selectedGuild);
  const { data: recentProfiles, isLoading: profilesLoading, error: profilesError, refetch: refetchProfiles } = useRecentProfiles(profileLimit);
  const { data: activityHistory, isLoading: activityLoading, error: activityError, refetch: refetchActivity } = useHotelBullActivityHistory(selectedGuild, 7);
  const { data: guildsData } = useDiscordGuilds();

  const guildsMap = useMemo(() => {
    const map = new Map<string, { name: string; icon: string | null }>();
    if (guildsData?.guilds) {
      for (const guild of guildsData.guilds) {
        map.set(guild.id, { name: guild.name, icon: guild.icon });
      }
    }
    return map;
  }, [guildsData]);

  const guilds = guildsData?.guilds ?? [];

  const { toggleProfile, deleteProfile, updateProfile, isLoading: profileOperationsLoading, error: profileOperationsError } = useProfileOperations();

  const handleToggleProfile = async (hashedUserId: string, guildId: string, currentlyEnabled: boolean) => {
    try {
      await toggleProfile.mutateAsync({ hashedUserId, guildId, enabled: !currentlyEnabled });
    } catch (error) {
      console.error('Failed to toggle profile:', error);
    }
  };

  const handleDeleteProfile = async (hashedUserId: string, guildId: string) => {
    if (window.confirm('Are you sure you want to delete this roast profile? This action cannot be undone.')) {
      try {
        await deleteProfile.mutateAsync({ hashedUserId, guildId });
        setIsModalOpen(false);
        setSelectedProfile(null);
      } catch (error) {
        console.error('Failed to delete profile:', error);
      }
    }
  };

  const handleOpenProfile = (profile: RoastProfile, guildName: string, userAvatarUrl: string, userName: string) => {
    setSelectedProfile({
      ...profile,
      personalityTraits: formatTraits(profile.personalityTraits),
      _guildName: guildName,
      _userAvatarUrl: userAvatarUrl,
      _userName: userName,
    });
    setIsModalOpen(true);
  };

  const handleSaveProfile = async (updates: { personalityTraits: string }) => {
    if (!selectedProfile?.hashedUserId || !selectedProfile?.guildId) return;

    await updateProfile.mutateAsync({
      hashedUserId: selectedProfile.hashedUserId,
      guildId: selectedProfile.guildId,
      personalityTraits: updates.personalityTraits,
    });

    setSelectedProfile(prev => prev ? { ...prev, personalityTraits: updates.personalityTraits } : null);
  };

  const calculatePercentage = (value: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <BarChart2Icon size={18} /> },
    { id: 'configuration', label: 'Configuration', icon: <SettingsIcon size={18} /> },
  ];

  const statusBadges = [
    { label: 'Bot Health', status: botHealth?.status },
    { label: 'Service', status: health?.status, showPulse: false },
  ];

  const rightContent = (
    <div className="flex items-center gap-3 w-full lg:w-auto">
      <Select
        value={selectedGuild || ''}
        onValueChange={(value) => setSelectedGuild(value || undefined)}
      >
        <SelectTrigger className="bg-panel-strong/40 border-stroke h-14 min-w-[240px] rounded-2xl shadow-lg backdrop-blur-md flex-1 lg:flex-none font-bold">
          <SelectValue placeholder="All Servers" />
        </SelectTrigger>
        <SelectContent className="rounded-2xl bg-panel-strong/80 backdrop-blur-xl border-stroke">
          <SelectItem value="" className="rounded-xl font-bold">All Servers ({guilds.length})</SelectItem>
          {guilds.map(guild => (
            <SelectItem key={guild.id} value={guild.id} className="rounded-xl">
              {guild.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="secondary"
        size="icon"
        className="h-14 w-14 rounded-2xl bg-panel-strong/40 border-stroke text-muted hover:text-text transition-all shadow-lg"
        onClick={() => {
          refetchStats();
          refetchProfiles();
          refetchActivity();
          refetchLlmStats();
        }}
        disabled={activeTab !== 'dashboard' || statsLoading || profilesLoading || activityLoading || llmStatsLoading}
      >
        <RefreshCw size={20} className={clsx((statsLoading || profilesLoading || activityLoading || llmStatsLoading) && "animate-spin")} />
      </Button>
    </div>
  );

  return (
    <BotPageLayout
      icon={<HotelIcon className="text-accent" size={36} />}
      iconColorClass="bg-accent/10 shadow-accent/10 border border-accent/20"
      title="Hotel Bull"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={(id) => setActiveTab(id as 'dashboard' | 'configuration')}
      statusBadges={statusBadges}
      rightContent={rightContent}
      maxWidth="1700px"
    >

      {activeTab === 'dashboard' && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
          {(healthError || botHealthError || statsError || profilesError || activityError || llmStatsError) && (
            <Alert variant="destructive" className="bg-red/5 border-red/20 shadow-2xl rounded-3xl p-6">
              <div className="flex items-center gap-4">
                <AlertCircle className="text-red" size={24} />
                <div>
                  <h4 className="font-black text-red uppercase tracking-widest text-[10px] mb-1">Data Sync Error</h4>
                  <p className="text-sm font-medium text-text/80">
                    Some data may be outdated. Please check your connection.
                  </p>
                </div>
              </div>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <StatCard
              watermarkIcon={<UserIcon />}
              title="Users"
            >
              <StatValue
                label="Total"
                value={stats?.totalUsers || 0}
                showProgress
                progressValue={85}
              />
              <StatMetric
                label="Active Today"
                value={stats?.activeToday || 0}
                icon={<TrendingUp />}
                trend="up"
              />
            </StatCard>

            <StatCard
              watermarkIcon={<Zap />}
              title="Activity"
            >
              <StatValue
                label="Global Roasts"
                value={stats?.totalRoasts || 0}
              />
              <div className="space-y-4">
                <div className="h-16 flex items-end gap-1">
                  {activityHistory?.history?.slice(-7).map((day, i) => {
                    const maxValue = Math.max(...activityHistory.history.map(d => d.conversationCount), 1);
                    const height = (day.conversationCount / maxValue) * 100;
                    return (
                      <div key={i} className="flex-1 relative group/bar">
                        <div className="bg-accent/30 group-hover:bg-accent rounded-full transition-all duration-300" style={{ height: `${Math.max(height, 10)}%` }} />
                      </div>
                    );
                  })}
                </div>
                <div className="text-[9px] font-bold text-muted/40 text-center tracking-widest">Last 7 Days</div>
              </div>
            </StatCard>

            <StatCard
              watermarkIcon={<History />}
              title="Statistics"
              colSpan={2}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8 p-2">
                <StatValue
                  label="Successful Syncs"
                  value="98.4%"
                  trend="+2.1% vs. last week"
                  indicatorColor="bg-accent"
                />
                <StatValue
                  label="Response Time"
                  value="142ms"
                  trend="Normal"
                  indicatorColor="bg-warning"
                />
                <StatValue
                  label="Messages Generated"
                  value="12.4K"
                  trend="Across all servers"
                  indicatorColor="bg-purple-500"
                />
              </div>
            </StatCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <Card className="lg:col-span-8 p-8 border-stroke bg-panel/30 backdrop-blur-md rounded-[3rem] shadow-2xl relative group">
              <div className="flex items-center justify-between mb-8 px-2">
                <div className="space-y-1">
                  <h2 className="text-2xl font-black text-text tracking-tight flex items-center gap-3">
                    <TagIcon className="text-accent" size={24} />
                    Recent Interactions
                  </h2>
                  <p className="text-xs text-muted font-medium italic">Recent user interactions processed by the bot.</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex bg-panel-strong/40 p-1 rounded-xl border border-stroke shadow-inner">
                    {[5, 10, 25].map(limit => (
                      <button
                        key={limit}
                        onClick={() => setProfileLimit(limit)}
                        className={clsx("px-3 py-1.5 rounded-lg text-xs font-black transition-all", profileLimit === limit ? "bg-accent text-bg-solid shadow-lg shadow-accent/20" : "text-muted hover:text-text")}
                      >
                        {limit}
                      </button>
                    ))}
                  </div>
                  <Button variant="secondary" size="icon" className="h-10 w-10 bg-panel-strong/40 border-stroke rounded-xl" onClick={() => refetchProfiles()}>
                    <RefreshCw size={14} className={clsx(profilesLoading && "animate-spin")} />
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {profilesLoading ? (
                  <div className="py-24 flex flex-col items-center justify-center text-muted gap-4">
                    <Loader2 className="animate-spin text-accent" size={32} />
                    <p className="text-[10px] font-black uppercase tracking-widest animate-pulse">Loading...</p>
                  </div>
                ) : recentProfiles?.map((profile) => {
                  const guildInfo = guildsMap.get(profile.guildId || '');
                  const guildName = guildInfo?.name || profile.serverName || 'Unknown Server';
                  const userName = profile.displayName || profile.username || 'Unknown User';
                  const userAvatarUrl = profile.avatarUrl || getDefaultAvatarUrl(0);

                  return (
                    <EntityListItem
                      key={`${profile.hashedUserId}-${profile.guildId}`}
                      avatarUrl={userAvatarUrl}
                      name={userName}
                      subtitle={guildName}
                      subtitleIcon={<ServerIcon size={10} />}
                      isEnabled={profile.isEnabled}
                      onToggle={() => handleToggleProfile(profile.hashedUserId || '', profile.guildId || '', profile.isEnabled)}
                      onDelete={() => handleDeleteProfile(profile.hashedUserId || '', profile.guildId || '')}
                      onClick={() => handleOpenProfile(profile, guildName, userAvatarUrl, userName)}
                      metadata={
                        <>
                          <div className="hidden md:flex flex-col items-end">
                            <span className="text-[9px] font-black text-muted/30 uppercase tracking-widest">ROASTS</span>
                            <span className="text-xs font-black text-text">{profile.analysisCount || 0}</span>
                          </div>
                          <div className="hidden sm:block text-[10px] font-bold text-muted/40 uppercase tracking-widest w-24 text-right">
                            {profile.updatedAt ? new Date(profile.updatedAt).toLocaleDateString() : 'N/A'}
                          </div>
                        </>
                      }
                    />
                  );
                })}
              </div>
            </Card>

            <div className="lg:col-span-4 space-y-8">
              <Card className="p-8 border-stroke bg-panel/30 backdrop-blur-md rounded-[3rem] shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                  <ActivityIcon size={120} />
                </div>
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted/60 mb-8">System Status</h3>
                <div className="space-y-6">
                  <div className="bg-panel-strong/40 rounded-3xl p-6 border border-stroke shadow-inner">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs font-bold text-text/80 uppercase tracking-widest">Bot Status</span>
                      <Badge className="bg-green/10 text-green border-green/20 text-[9px] font-black">Healthy</Badge>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between text-[10px] font-bold text-muted/40 uppercase">
                        <span>Active Users</span>
                        <span>{calculatePercentage(stats?.activeToday || 0, stats?.totalUsers || 1)}%</span>
                      </div>
                      <Progress value={calculatePercentage(stats?.activeToday || 0, stats?.totalUsers || 1)} className="h-1.5 bg-panel-soft" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-panel-strong/40 rounded-2xl p-4 border border-stroke/40 shadow-inner">
                      <span className="text-[9px] font-black text-muted/40 uppercase block mb-1">Errors</span>
                      <span className="text-lg font-black text-text">0</span>
                    </div>
                    <div className="bg-panel-strong/40 rounded-2xl p-4 border border-stroke/40 shadow-inner">
                      <span className="text-[9px] font-black text-muted/40 uppercase block mb-1">LLM Health</span>
                      <span className="text-lg font-black text-green">100%</span>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-8 border-stroke bg-panel/30 backdrop-blur-md rounded-[3rem] shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                  <History size={120} />
                </div>
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted/60 mb-8">Model Usage</h3>
                <div className="space-y-4">
                  {llmStats?.usage?.slice(0, 3).map((stat, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-panel-strong/40 rounded-2xl border border-stroke/40">
                      <div className="flex flex-col">
                        <span className="text-[9px] font-black text-accent uppercase tracking-widest">{stat.model}</span>
                        <span className="text-xs font-bold text-text/60 italic">{stat.provider}</span>
                      </div>
                      <Badge className="bg-panel-soft text-text text-[10px] font-black border-stroke">{stat.totalRequests} USES</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'configuration' && <HotelBullConfigPage />}

      <ProfileModal
        profile={selectedProfile}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProfile(null);
        }}
        onSave={handleSaveProfile}
        onToggle={() => {
          if (selectedProfile?.hashedUserId && selectedProfile?.guildId) {
            handleToggleProfile(selectedProfile.hashedUserId, selectedProfile.guildId, selectedProfile.isEnabled ?? false);
            setSelectedProfile(prev => prev ? { ...prev, isEnabled: !prev.isEnabled } : null);
          }
        }}
        onDelete={() => {
          if (selectedProfile?.hashedUserId && selectedProfile?.guildId) {
            handleDeleteProfile(selectedProfile.hashedUserId, selectedProfile.guildId);
          }
        }}
        isLoading={profileOperationsLoading}
        guildName={selectedProfile?._guildName || 'Unknown Server'}
        userAvatarUrl={selectedProfile?._userAvatarUrl || getDefaultAvatarUrl(0)}
        userName={selectedProfile?._userName || 'Unknown'}
      />
    </BotPageLayout>
  );
};
